import { MButton } from '@assets/mui/@material-extend';
import palette from '@assets/theme/palette';
import CloseIcon from '@mui/icons-material/Close';
import { Tooltip } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { customsTaxService } from '@services/requests/customsTaxService';
import {
  CountriesResponse,
  ExportCalculationRateResponse,
  ImportCalculationRateResponse,
} from '@services/requests/customsTaxService/interface';
import { CurrencyData } from '@services/requests/searchLoaders/currencyLoader/CurrencyLoaderQuery';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ServiceProps } from '../../types';
import { CustomsPaymentsCodeSelect } from '../CustomsPaymentsCodeSelect';
import {
  checkErrorExisting,
  mouseWheelInputChangePreventHandler,
  validateCustomsPaymentProducts,
} from '../helpers/helpers';
import { CustomsPaymentsTemplateProduct, CustomsPaymentsTemplateStore } from '../types';
import { AdditionalTaxList } from './AdditionalTax';
import { CountFields } from './CountFields';
import { ProductHelp } from './ProductHelp';
import { ResultTables } from './ResultTables';
import { Container } from './StyledComponents';
import { AdditionalTax, isResponseValidateErrorPredicate } from './types';

interface CustomsPaymentsProductItemProps extends ServiceProps {
  countries: CountriesResponse;
  productIndex: number;
  isLastProduct: boolean;
  totalContractCurrencyCost: number;
  totalProductsWeight: number;
  currencies: CurrencyData[];
}

function CustomsPaymentsProductItem({
  productIndex,
  isLastProduct,
  countries,
  totalProductsWeight,
  totalContractCurrencyCost,
  currencies,
  ...baseProps
}: CustomsPaymentsProductItemProps) {
  const {
    variant,
    service,
    globalFields,
    servicesFields,
    serviceTemplateStore,
    options,
    globalFieldValues,
    servicesFieldValues,
    isOrderDisplay,
    onChangeServiceTemplateStore,
  } = baseProps;

  const store: CustomsPaymentsTemplateStore = serviceTemplateStore[service.id];
  const product = store.products[productIndex];

  const { t } = useTranslation(['CustomsPayments']);

  const [additionalTax, setAdditonalTax] = useState<AdditionalTax | null>(null);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);

  const fields = [...servicesFields, ...globalFields];
  const fieldValues = { ...globalFieldValues, ...servicesFieldValues };
  const settings = service.settings.TemplateSettings.CustomsPayments;

  const customsModeField = fields.find((f) => f.code === settings.CustomsModeField);
  const borderDeliverySumField = fields.find((f) => f.code === settings.BorderDeliverySumField);

  const countryField = fields.find((f) => f.code === settings.CountryField);

  const {
    productId,
    codeDescription,
    contractCurrencyCost,
    country,
    originCertificateExisting,
    weight,
    selectedFee,
    selectedAntidumpingFee,
    selectedExcise,
    selectedExportFee,
    selectedVat,
    isCalculated,
    invalidFields,
    code,
  } = product;

  const customsMode =
    options[customsModeField.code].find(
      (option) => option.value === fieldValues[customsModeField.code]
    )?.origin?.value ?? 1;

  const isExport = customsMode === 2;

  const autocompleteFormattedCountries = (countries || []).map((country) => ({
    value: country.Code,
    label: country.Name[0] + country.Name.slice(1).toLowerCase(),
  }));

  const selectedCountry = autocompleteFormattedCountries.find((c) => c.value === country);

  const borderDeliverySum = fieldValues[borderDeliverySumField.code];

  const isProductsCountFieldVisible = [
    selectedFee?.ValueDetail?.ValueUnit,
    selectedFee?.ValueDetail?.ValueUnit2,
    selectedFee?.ValueDetail?.ValueUnit3,
    selectedFee?.ValueDetailAdd?.ValueUnit,
    selectedExportFee?.ValueDetail?.ValueUnit,
    selectedExportFee?.ValueDetail?.ValueUnit2,
  ]
    .filter(Boolean)
    .reduce((_, unit) => !unit.includes('%'), false);

  const isBaseCountVisible =
    !product.selectedExportFee ||
    (!!product.selectedExportFee && !product.selectedExportFee?.Value.includes('Беспошлинно'));

  useEffect(() => {
    if (country.length > 0 || !countryField) {
      return;
    }

    changeOriginCountryHandler(fieldValues[countryField.code].toString());
    resetFieldsHandler();
    resetTaxParamsHandler();
  }, [country]);

  useEffect(() => {
    if (
      store.products.length > 0 &&
      typeof borderDeliverySum === 'number' &&
      typeof isLastProduct === 'boolean'
    ) {
      highlightInvalidFieldsHandler(['contractCurrencyCost', 'code', 'weight']);
    }
  }, [store.products.length, isLastProduct, borderDeliverySum]);

  const countryTypeText =
    customsMode === 1
      ? t('CustomsPayments:OriginCountryPlaceholder')
      : t('CustomsPayments:DestinationCountryPlaceholder');

  const isSpecialFieldsVisible = typeof selectedExcise?.Prim === 'string';
  const isExciseCountFieldVisible =
    !!selectedExcise && selectedExcise?.Measure?.Razm !== selectedFee?.Measure?.Razm;
  const isSingleProduct = store.products.length === 1;

  const errors = useMemo(() => {
    const errors = invalidFields ?? [];
    const filteredErrors = errors.filter((key) => {
      if (
        !isSpecialFieldsVisible &&
        (key === 'packagesCount' || key === 'maximumRetailPackagePrice')
      ) {
        return false;
      }
      if (!isExciseCountFieldVisible && key === 'exciseCount') {
        return false;
      }
      if ((isSingleProduct || borderDeliverySum === 0) && key === 'weight') {
        return false;
      }
      if (!isBaseCountVisible || (!isProductsCountFieldVisible && key === 'productsCount')) {
        return false;
      }
      return true;
    });

    return filteredErrors;
  }, [invalidFields, productIndex, isSpecialFieldsVisible]);

  const getRatesBtnTextColor = checkErrorExisting(['isCalculated'], errors)
    ? palette.light.error.main
    : palette.light.secondary.main;

  const changeProductValueHandler = <T extends keyof CustomsPaymentsTemplateProduct>(
    key: T,
    value: CustomsPaymentsTemplateProduct[T]
  ) => {
    const newStore = { ...store };
    const foundProductIndex = newStore.products.findIndex(
      (product) => product.productId === productId
    );

    if (foundProductIndex === -1) {
      return;
    }

    const changedProduct = { ...newStore.products[foundProductIndex] };
    changedProduct[key] = value;
    changedProduct.invalidFields = changedProduct.invalidFields.filter(
      (errorKey) => errorKey !== key
    );

    newStore.products.splice(foundProductIndex, 1, changedProduct);

    onChangeServiceTemplateStore(variant.id, service.id, { ...newStore });
  };

  const changeOriginCountryHandler = (value: typeof selectedCountry | string) => {
    const newStore = { ...store };

    const foundProductIndex = newStore.products.findIndex(
      (product) => product.productId === productId
    );

    if (foundProductIndex === -1) {
      return;
    }

    const changedProduct = { ...newStore.products[foundProductIndex] };
    changedProduct.country = typeof value === 'string' ? value : value?.value;
    changedProduct.invalidFields = changedProduct.invalidFields.filter(
      (errorKey) => errorKey !== 'country'
    );

    newStore.products.splice(productIndex, 1, changedProduct);

    onChangeServiceTemplateStore(variant.id, service.id, { ...newStore });
  };

  const removeCustomsPaymentProductHandler = (index: number) => {
    const newStore = { ...store };

    const storeWithRemovedProduct = newStore.products.filter((_, i) => i !== index);

    onChangeServiceTemplateStore(variant.id, service.id, {
      ...newStore,
      products: storeWithRemovedProduct,
    });
  };

  const highlightInvalidFieldsHandler = (filterErrorKeys: string[]) => {
    const newStore = { ...store };

    const productErrors = validateCustomsPaymentProducts(
      newStore.products,
      filterErrorKeys,
      service,
      fields,
      fieldValues
    );
    const errors = productErrors.map((item) => [...item]);

    const changedProduct = { ...newStore.products[productIndex] };
    changedProduct.invalidFields = errors[productIndex];
    newStore.products.splice(productIndex, 1, changedProduct);

    onChangeServiceTemplateStore(variant.id, service.id, { ...newStore });

    return errors[productIndex];
  };

  const resetFieldsHandler = () => {
    changeProductValueHandler('productsCount', '0');
    changeProductValueHandler('maximumRetailPackagePrice', '0');
    changeProductValueHandler('packagesCount', '0');
    changeProductValueHandler('exciseCount', '0');
  };

  const resetTaxParamsHandler = () => {
    changeProductValueHandler('selectedFee', null);
    changeProductValueHandler('selectedAntidumpingFee', null);
    changeProductValueHandler('selectedExcise', null);
    changeProductValueHandler('selectedVat', null);
    changeProductValueHandler('selectedExportFee', null);
    setAdditonalTax(null);
    changeProductValueHandler('isCalculated', false);
    changeProductValueHandler('sborList', []);
    changeProductValueHandler('sborListImport', []);
    changeProductValueHandler('total', {});
  };

  useEffect(() => {
    if (isOrderDisplay) {
      return;
    }

    if (customsMode) {
      resetFieldsHandler();
      resetTaxParamsHandler();
    }
  }, [customsMode]);

  const calculateAdditionalTaxHandler = async () => {
    const errors = highlightInvalidFieldsHandler(['contractCurrencyCost', 'code', 'weight']);
    const isCodeError =
      errors.includes('code') ||
      errors.includes('contractCurrencyCost') ||
      errors.includes('weight');

    if (isCalculated || isCodeError) {
      return;
    }

    setIsCalculating(true);
    const formattedCustomsMode = customsMode === 1 ? 'import' : 'export';
    const formattedCertificate = originCertificateExisting ? '1' : '0';

    try {
      const result = await customsTaxService().CalculateRates(formattedCustomsMode, {
        mode: formattedCustomsMode,
        tncode: code,
        certificate: formattedCertificate,
        country,
      });

      const resultSbor = await customsTaxService().LoadSbor({
        mode: formattedCustomsMode,
        tnved: code,
      });
      const resultImportSbor = await customsTaxService().LoadSbor({
        mode: 'import',
        tnved: code,
      });

      if (isResponseValidateErrorPredicate(result) || isResponseValidateErrorPredicate(result)) {
        setIsCalculating(false);
        return;
      }

      const fees =
        formattedCustomsMode === 'import'
          ? (result as ImportCalculationRateResponse).Importlist.Import
          : [];
      const formattedFees = Array.isArray(fees) ? fees : [fees];

      const exportFees =
        formattedCustomsMode === 'export'
          ? (result as ExportCalculationRateResponse).Exportlist.Export
          : [];
      const formattedExportFees = Array.isArray(exportFees) ? exportFees : [exportFees];
      const formattedUniqueExportFees = formattedExportFees.map((exportFee, index) => ({
        ...exportFee,
        Value: exportFee.Value + '|' + index,
      }));

      const vats =
        formattedCustomsMode === 'import'
          ? (result as ImportCalculationRateResponse).VATlist.VAT
          : [];
      const formattedVats = Array.isArray(vats) ? vats : [vats];
      const foundDefaultVat = formattedVats?.find(
        (vat) => vat.Value.includes('22%') && typeof vat.Condition !== 'object'
      );
      const formattedDefaultVat = foundDefaultVat
        ? {
            ...foundDefaultVat,
            Directory: null,
            Condition: '',
          }
        : null;
      const formattedUniqueVats = [formattedDefaultVat, ...formattedVats]
        .filter(Boolean)
        .map((vat, index) => ({
          ...vat,
          Value: vat.Value + '|' + index,
        }));

      const excises = result.Exciselist?.Excise;
      let formattedUniqueExices = [];
      if (excises) {
        const formattedExices = Array.isArray(excises) ? excises : [excises];
        formattedUniqueExices = formattedExices.map((vat, index) => ({
          ...vat,
          Value: vat.Value + '|' + index,
        }));
      }

      const antidumpingFees = formattedFees?.filter(
        (fee) => fee?.Note === 'Антидемпинговая пошлина'
      );
      const regularFees = formattedFees?.filter((fee) => fee?.Note !== 'Антидемпинговая пошлина');

      const maxAntidumpingPercent = Math.max(
        ...antidumpingFees.map((f) => parseFloat(f.Value)).filter(Boolean)
      );
      const foundAntidumpingMaxPercent = antidumpingFees.find(
        (f) => f.Value === `${maxAntidumpingPercent}%`
      );

      const maxPercent = Math.max(...regularFees.map((f) => parseFloat(f.Value)).filter(Boolean));
      const foundMaxPercent = antidumpingFees.find((f) => f.Value === `${maxPercent}%`);

      const defaultSelectedFee = foundMaxPercent ?? formattedFees?.[0] ?? null;
      const defaultSelectedAntidumpingFee =
        foundAntidumpingMaxPercent ?? antidumpingFees?.[0] ?? null;
      const defaultSelectedExportFee = formattedUniqueExportFees?.[0] ?? null;
      const defaultSelectedVat =
        formattedUniqueVats?.find((vat) => vat.Value.includes('22%')) ??
        formattedUniqueVats?.[0] ??
        null;
      const defaultSelectedExcise = formattedUniqueExices?.[0] ?? null;

      setAdditonalTax({
        excises: formattedUniqueExices,
        fees: formattedFees,
        vats: formattedUniqueVats,
        exportFees: formattedUniqueExportFees,
      });

      changeProductValueHandler('selectedFee', defaultSelectedFee);
      changeProductValueHandler('selectedAntidumpingFee', defaultSelectedAntidumpingFee);
      changeProductValueHandler('selectedExcise', defaultSelectedExcise);
      changeProductValueHandler('selectedVat', defaultSelectedVat);
      changeProductValueHandler('selectedExportFee', defaultSelectedExportFee);
      changeProductValueHandler('isCalculated', true);
      changeProductValueHandler('sborList', resultSbor);
      changeProductValueHandler('sborListImport', resultImportSbor);
      setIsCalculating(false);

      highlightInvalidFieldsHandler([
        'productsCount',
        'packagesCount',
        'maximumRetailPackagePrice',
        'weight',
        'contractCurrencyCost',
        'exciseCount',
      ]);
    } catch (e) {
      setIsCalculating(false);
    }
  };

  return (
    <Container>
      <div className="product-header-wrap">
        <div className="title">
          {t('CustomsPayments:ProductTitle')} #{productIndex + 1}
        </div>
        {!isLastProduct && (
          <IconButton
            size={'small'}
            onClick={() => removeCustomsPaymentProductHandler(productIndex)}
          >
            <CloseIcon sx={{ fontSize: 25 }} />
          </IconButton>
        )}
      </div>
      <div className="main-config-wrap">
        <Autocomplete
          fullWidth
          disablePortal
          id="origin-country"
          className="origin-country"
          value={selectedCountry || null}
          disableClearable={true}
          onChange={(_, value) => {
            changeOriginCountryHandler(value);
            resetFieldsHandler();
            resetTaxParamsHandler();
          }}
          options={autocompleteFormattedCountries}
          renderInput={(params) => (
            <TextField
              {...params}
              error={checkErrorExisting(['country'], errors)}
              label={countryTypeText}
            />
          )}
        />
        <CustomsPaymentsCodeSelect
          onChangeCode={changeProductValueHandler}
          onResetTaxParams={resetTaxParamsHandler}
          onResetFields={resetFieldsHandler}
          errors={errors}
          codeDescription={codeDescription}
        />
        <TextField
          fullWidth
          size={'medium'}
          type={'text'}
          autoComplete="off"
          className="cost-field-input"
          error={checkErrorExisting(['contractCurrencyCost'], errors)}
          label={t('CustomsPayments:PricePlaceholder')}
          variant="outlined"
          value={contractCurrencyCost}
          onChange={(event) => {
            const numberRegex = /^([0-9]|\s)+(\.|,)?([0-9]|\s)*$/;
            if (!numberRegex.test(event.target.value)) {
              return;
            }
            changeProductValueHandler(
              'contractCurrencyCost',
              event.target.value.replace(',', '.').replace(/\s/g, '')
            );
          }}
          onWheel={mouseWheelInputChangePreventHandler}
        />
        {!isLastProduct && borderDeliverySum !== 0 && (
          <TextField
            fullWidth
            helperText={
              checkErrorExisting(['weight'], errors) && t('CustomsPayments:GrossWeightError')
            }
            size={'medium'}
            type={'number'}
            autoComplete="off"
            className="cost-field-input"
            error={checkErrorExisting(['weight'], errors)}
            label={t('CustomsPayments:GrossWeightPlaceholder')}
            variant="outlined"
            value={weight}
            onChange={(event) => {
              changeProductValueHandler('weight', event.target.value);
            }}
            onWheel={mouseWheelInputChangePreventHandler}
          />
        )}
      </div>
      <div>
        <Tooltip disableInteractive title={t('CustomsPayments:CertificateOriginAvailableTooltip')}>
          <FormControlLabel
            className="origin-certificate"
            label={t('CustomsPayments:CertificateOriginAvailable')}
            control={
              <Checkbox
                checked={originCertificateExisting}
                onChange={(event) => {
                  changeProductValueHandler('originCertificateExisting', event.target.checked);
                  resetFieldsHandler();
                  resetTaxParamsHandler();
                }}
                size={'small'}
              />
            }
          />
        </Tooltip>
      </div>
      <div>
        <MButton
          className={'get-rates-btn'}
          variant={'outlined'}
          color={checkErrorExisting(['isCalculated'], errors) ? 'error' : 'secondary'}
          fullWidth={true}
          onClick={calculateAdditionalTaxHandler}
        >
          {isCalculating && (
            <CircularProgress
              size={20}
              style={{ color: palette.light.secondary.main, marginRight: '20px' }}
            />
          )}
          <Typography color={getRatesBtnTextColor} fontSize={14}>
            {t('CustomsPayments:CalculatePaymentsBtn')}
          </Typography>
        </MButton>
      </div>
      {additionalTax && isCalculated && (
        <AdditionalTaxList
          additionalTax={additionalTax}
          selectedExcise={selectedExcise}
          selectedFee={selectedFee}
          selectedExportFee={selectedExportFee}
          selectedVat={selectedVat}
          selectedAntidumpingFee={selectedAntidumpingFee}
          onChangeProductValue={changeProductValueHandler}
          onHighlightInvalidFields={highlightInvalidFieldsHandler}
        />
      )}

      {isCalculated && (
        <CountFields
          invalidFields={invalidFields}
          selectedExcise={selectedExcise}
          selectedFee={selectedFee}
          productIndex={productIndex}
          variant={variant}
          service={service}
          serviceTemplateStore={serviceTemplateStore}
          onChangeProductValue={changeProductValueHandler}
          onChangeServiceTemplateStore={onChangeServiceTemplateStore}
        />
      )}

      {isCalculated && (
        <ResultTables
          currencies={currencies}
          selectedExcise={selectedExcise}
          selectedFee={selectedFee}
          selectedAntidumpingFee={selectedAntidumpingFee}
          selectedVat={selectedVat}
          selectedExportFee={selectedExportFee}
          isExport={isExport}
          globalFieldValues={globalFieldValues}
          globalFields={globalFields}
          servicesFieldValues={servicesFieldValues}
          servicesFields={servicesFields}
          options={options}
          productIndex={productIndex}
          service={service}
          variant={variant}
          serviceTemplateStore={serviceTemplateStore}
          totalContractCurrencyCost={totalContractCurrencyCost}
          totalProductsWeight={totalProductsWeight}
          onChangeServiceTemplateStore={onChangeServiceTemplateStore}
        />
      )}

      <ProductHelp
        product={product}
        productIndex={productIndex}
        customsMode={customsMode}
        service={service}
        variant={variant}
        serviceTemplateStore={serviceTemplateStore}
        onChangeServiceTemplateStore={onChangeServiceTemplateStore}
      />
    </Container>
  );
}

export { CustomsPaymentsProductItem };
